import { Modal, ModalContent, ModalDescription, ModalHeader, ModalTitle, Text } from '@mntn/ui-components'
import { useCallback } from 'react'
import { Button, OfferingDetailSection } from '@/components/shared'
import { SELECT_THEME_CLASSNAME } from '@/components/shared/mfe-theme-wrapper'
import type {
  ArchiveCampaignCallback,
  CreateCampaignCallbacks,
  DuplicateCampaignCallback,
} from '@/components/shared/types'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'
import { useCreateCampaignHandler } from '@/hooks'
import { CampaignsSection } from './campaigns-section'
import { OfferingSummarySection } from './offering-summary-section'

interface Props extends CreateCampaignCallbacks {
  selectedOrderItem: OrderItem
  onClose: () => void
  onDuplicateCampaign: DuplicateCampaignCallback
  onArchiveCampaign: ArchiveCampaignCallback
  open: boolean
  isLoading?: boolean
  isDisabled?: boolean
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const DetailModal: React.FC<Props> = ({
  selectedOrderItem,
  onClose,
  onCreateCampaignV2,
  onDuplicateCampaign,
  onArchiveCampaign,
  open,
  isLoading = false,
}) => {
  const { handleCreateCampaign, handleCreateExperiment, isCreating } = useCreateCampaignHandler({
    onCreateCampaignV2,
    orderItem: selectedOrderItem,
  })
  const handleDuplicateCampaign = useCallback(
    async (campaignId: number, isExperiment?: boolean) => {
      try {
        // Close modal immediately when duplicate is triggered
        onClose()

        const result = await onDuplicateCampaign(campaignId, isExperiment)
        return result
      } catch (error) {
        console.error('Flight Deck: Campaign duplication failed:', error)
        throw error
      }
    },
    [onDuplicateCampaign, onClose],
  )

  const handleArchiveCampaign = useCallback(
    async (campaignId: number) => {
      try {
        // Close modal immediately when archive is triggered
        onClose()

        const result = await onArchiveCampaign(campaignId)
        return result
      } catch (error) {
        console.error('Flight Deck: Campaign archive failed:', error)
        throw error
      }
    },
    [onArchiveCampaign, onClose],
  )

  return (
    <Modal open={open} onOpenChange={onClose}>
      {/* note: this component is rendered at the end of the document body so we need to pass in the prefix and theme classes to get it properly styled */}
      <ModalContent
        className="bg-background flex flex-col w-full md:min-w-[960px] md:max-h-[85vh] p-0 text-header"
        wrapperClassName={SELECT_THEME_CLASSNAME}
      >
        <ModalHeader className="px-6 pt-6">
          <ModalTitle>
            <div className="flex flex-col gap-1">
              <Text inline variant="h3">
                {selectedOrderItem.offeringName}
              </Text>
              <ModalDescription>
                <Text inline variant="base-sm" className="text-secondary">
                  Deal details and campaigns assigned to this inventory.
                </Text>
              </ModalDescription>
            </div>
          </ModalTitle>
        </ModalHeader>
        <div className="overflow-y-auto flex-1">
          <div className="flex flex-col gap-6 px-6">
            <OfferingSummarySection selectedOffering={selectedOrderItem} />
            <CampaignsSection
              selectedOffering={selectedOrderItem}
              onCreateCampaign={async input => {
                onClose() // Close modal first
                return await handleCreateCampaign(input) // Then trigger campaign creation
              }}
              onCreateExperiment={async input => {
                await handleCreateExperiment(input)
                onClose()
              }}
              onDuplicateCampaign={handleDuplicateCampaign}
              onArchiveCampaign={handleArchiveCampaign}
              isLoading={isLoading || isCreating}
            />
            <OfferingDetailSection
              programs={selectedOrderItem.programs}
              description={selectedOrderItem.offeringDescription}
            />
          </div>
        </div>
        <div className="flex justify-end px-6 py-4 border-t border-surface-bg">
          <Button type="button" size="small" variant="core" onClick={onClose} disabled={isLoading}>
            Done
          </Button>
        </div>
      </ModalContent>
    </Modal>
  )
}
